import { MouseEventHandler } from "react";
import { Divider, Tag } from "antd";
import { useNavigate } from "react-router-dom";

import FaIcon from "../../global/FaIcon";
import NextSpinner from "../NextSpinner";
import NextRestrictedElement from "../NextRestrictedElement";
import { EMPTY_TEXT } from "../../../utils/enum/defaultParams.enum";

declare type Position = "left" | "middle" | "right";
export declare type TypeButton = "primary" | "default" | "error" | "success" | "dark" | "warning" | "orange";
export declare type HtmlTypeButton = "submit" | "button";

type Props = {
  label?: string;
  icon?: string | undefined;
  onClick?: MouseEventHandler<HTMLElement> | undefined;
  position?: Position | undefined;
  type?: TypeButton | undefined;
  htmlType?: HtmlTypeButton | undefined;
  restriction?: string | undefined;
  refTo?: string | undefined;
  className?: string | undefined;
  loading?: boolean | undefined;
  disabled?: boolean | undefined;
  replaceUrl?: boolean | undefined;
  hasPreviousDivider?: boolean | undefined;
  timer?: string | undefined;
  chevron?: boolean | undefined;
  chevronIcon?: string | undefined;
};

const styleType = {
  default: "default-button",
  primary: "primary-button",
  success: "success-button",
  error: "error-button",
  dark: "dark-button",
  warning: "warning-button",
  orange: "orange-button",
};

const NextButton = ({
  label,
  icon,
  position,
  type = "default",
  htmlType = "button",
  onClick,
  refTo,
  replaceUrl = false,
  className = EMPTY_TEXT,
  loading = false,
  disabled = false,
  restriction,
  hasPreviousDivider = false,
  timer,
  chevron = false,
  chevronIcon = "chevron-down",
}: Props) => {
  const navigate = useNavigate();

  let _classPosition = EMPTY_TEXT;
  let _classDisabled = EMPTY_TEXT;

  if (position === "left") _classPosition = "mr-auto";
  if (position === "right") _classPosition = "ml-auto";
  if (position === "middle") _classPosition = "mr-auto ml-auto";

  const btnClasses = `next-button ${styleType[type]} ${_classPosition} ${_classDisabled} ${className}`;

  const onPressedButton: MouseEventHandler<HTMLElement> = (event) => {
    if (onClick) onClick(event);
    if (refTo) navigate(refTo, { replace: replaceUrl });
  };

  const childrenButton = (
    <>
      {icon && <i className={`far fa-${icon} fa-fw mr-2 ml-0`} />}
      <span>{label}</span>
      {chevron && <FaIcon icon={chevronIcon ?? "chevron-down"} classes="ml-2 mr-0" />}
    </>
  );

  const buildButton = (
    <>
      {hasPreviousDivider && <Divider type="vertical" />}
      <NextSpinner spinning={loading} disabled={disabled}>
        <button className={btnClasses} onClick={onPressedButton} type={htmlType} disabled={disabled}>
          {childrenButton}
          {timer && (
            <Tag className="ml-2 -mr-2" color="blue">
              <FaIcon icon="timer" classes="mr-1" />
              {timer}
            </Tag>
          )}
        </button>
      </NextSpinner>
    </>
  );

  if (restriction) {
    return <NextRestrictedElement restriction={restriction}>{buildButton}</NextRestrictedElement>;
  }

  return buildButton;
};

export default NextButton;
